import { useState, useEffect } from "react";
import { ReactGrid, NonEditableCell, TextCell } from "@silevis/reactgrid";
import { format } from "date-fns";

import CellStatus from "./CellStatus";
import { getRows, getColumns } from "./helper";
import dates from "tools/dates";
import funcs from "tools/functions";
import "./Sheet.css";

const COL_WIDTH = 95;

function setHeaderMonth(months, rowIdx, res) {
  let colIndex = 1;
  for (const month of months) {
    res.push({
      rowIndex: rowIdx,
      colIndex: colIndex,
      colSpan: month.colSpan,
      Template: TextCell,
      props: { text: month.value, style: style.month },
    });
    colIndex += month.colSpan;
  }
}

function setStatusRow(recordStatus, rowIdx, res) {
  let colIdx = 1;
  res.push({
    rowIndex: rowIdx,
    colIndex: 0,
    Template: TextCell,
    props: { text: "Room Status", style: style.room_status },
  });
  for (const rec of recordStatus) {
    res.push({
      rowIndex: rowIdx,
      colIndex: colIdx,
      Template: () => CellStatus(rec),
      props: { text: rec.value, style: style.cell_status },
    });
    colIdx += 1;
  }
}

function setBlankRow(headers, rowIdx, res) {
  res.push({
    rowIndex: rowIdx,
    colIndex: 0,
    Template: NonEditableCell,
    colSpan: headers.length + 1,
  });
}

function setHeaderRow(headers, record, rowIdx, res) {
  let colIdx = 0;
  res.push({
    rowIndex: rowIdx,
    colIndex: 0,
    Template: NonEditableCell,
    props: { value: record.value, style: style.header_acco },
  });

  for (const dt of headers) {
    colIdx += 1;
    const value = dates.getTrytonDate2Js(dt.value);
    const fmtValue = format(value, "dd eee");
    res.push({
      rowIndex: rowIdx,
      colIndex: colIdx,
      Template: NonEditableCell,
      props: { value: fmtValue, style: style.header_day, width: COL_WIDTH },
    });
  }
}

function Sheet(props) {
  const { data, onSave } = props;
  const [cells, setCells] = useState([]);
  const { headDays, headMonths, records } = data;
  const columns = getColumns(headDays);
  const rows = getRows(records);

  function validateData(newValue) {
    const _value = newValue.toString().replace(/\./g, "");
    if (isNaN(_value)) return false;
    return true;
  }

  function handleChange(id, key, newValue) {
    if (!validateData(newValue)) return;
    setCells((prev) => {
      return prev.map((p) => {
        if (p.id === id) {
          const unit_price = parseInt(newValue);
          p.props[key] = funcs.fmtMoneyLocal(unit_price, 0);
          onSave({ id, unit_price });
        }
        return p;
      });
    });
  }

  function getCells() {
    const res = [];
    let rowIdx = 0;
    const stylePriceList = style.price_list;
    const stylePriceListBg = {
      ...style.price_list,
      backgroundColor: "rgb(241 245 249)",
    };
    const stylePrice = style.price;
    const stylePriceBg = {
      ...style.price,
      backgroundColor: "rgb(241 245 249)",
    };
    setHeaderMonth(headMonths, rowIdx, res);
    rowIdx += 1;
    for (const record of records) {
      setHeaderRow(headDays, record, rowIdx, res);
      rowIdx += 1;
      setStatusRow(record.status, rowIdx, res);
      let rowBg = 0;
      for (const rowPrices of record.prices) {
        let colIdx = 0;
        rowIdx += 1;
        let _style;
        let _stylePrice;
        const listPrice = rowPrices[0];
        if (rowBg % 2 === 0) {
          _style = stylePriceListBg;
          _stylePrice = stylePriceBg;
        } else {
          _style = stylePriceList;
          _stylePrice = stylePrice;
        }
        rowBg += 1;
        res.push({
          rowIndex: rowIdx,
          colIndex: colIdx,
          Template: NonEditableCell,
          props: { value: listPrice.value, style: _style },
        });
        for (const price of rowPrices.slice(1)) {
          colIdx += 1;
          res.push({
            rowIndex: rowIdx,
            colIndex: colIdx,
            Template: TextCell,
            id: price.id,
            props: {
              text: funcs.fmtMoneyLocal(parseInt(price.value), 0),
              style: _stylePrice,
              onTextChanged: (newText) =>
                handleChange(price.id, "text", newText),
            },
          });
        }
      }
      rowIdx += 1;
      setBlankRow(headDays, rowIdx, res);
      rowIdx += 1;
    }
    return res;
  }

  useEffect(() => {
    async function setCellsData() {
      const _cells = getCells(headDays, records);
      setCells(_cells);
    }
    setCellsData();
  }, [props.data]);

  return (
    <div
      id="hotel-calendar-rg-content"
      className="pb-2 pr-2 !overflow-scroll w-full"
    >
      <ReactGrid
        rows={rows}
        columns={columns}
        cells={cells}
        styles={rgStyles}
      />
    </div>
  );
}

const style = {
  header_acco: {
    color: "rgb(100, 116, 139)",
    backgroundColor: "rgb(253, 230, 138)",
    display: "flex",
    justifyContent: "left",
    fontWeight: "bold",
    paddingLeft: "15px",
    width: "auto",
    whiteSpace: "nowrap",
  },
  header_day: {
    color: "rgb(100 116 139)",
    backgroundColor: "rgb(245 245 245)",
    display: "flex",
    justifyContent: "center",
    fontSize: "0.875rem",
  },
  price_list: {
    color: "rgb(14, 116, 144)",
    display: "flex",
    justifyContent: "right",
    paddingRight: "5px",
    fontSize: "0.875rem",
  },
  price: {
    color: "rgb(38, 38, 38)",
    display: "flex",
    justifyContent: "center",
    paddingRight: "5px",
    fontSize: "0.875rem",
  },
  month: {
    display: "flex",
    justifyContent: "left",
    paddingLeft: 15,
    color: "rgb(115 115 115)",
    backgroundColor: "rgb(245 245 245)",
    fontWeight: "bold",
    fontSize: "0.875rem",
  },
  room_status: {
    color: "rgb(115, 115, 115)",
    display: "flex",
    justifyContent: "right",
    paddingRight: "5px",
    width: "auto",
  },
  cell_status: {
    display: "flex",
    width: "auto",
  },
};

const rgStyles = {
  // gridContent: {
  //   padding: 4,
  //   background: "rgb(255, 255, 255)",
  // },
  // selectionIndicator: {
  //   background: "rgba(144, 238, 144, 0.1)",
  //   border: {
  //     color: "#81df9b",
  //     style: "solid",
  //   },
  // },
};

export default Sheet;
